import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'
import 'rxjs/add/operator/map'

import { environment } from '../../environments/environment'
import { CrudSearchResult, CrudSearchResultMeta } from '../interfaces/crud.service'
import { Topic } from '../interfaces'
import { QueryString } from '../models'
import { AuthService } from './auth.service'

// The legacy list sorts by `date` (a {year, month, date} subdocument) while v2 sorts by
// `dateTime`, so the date column is the only sort key that has to be renamed.
const SORT_FIELDS: { [prop: string]: string } = {
  date: 'dateTime',
  dateTime: 'dateTime',
  type: 'type',
  title: 'title'
}

export interface AutocompleteItem {
  _id: string
  label: string
}

/**
 * Client for the v2 (NestJS) topics list.
 *
 * Only the list is migrated: the topic detail page and the recipients modal still go
 * through TopicService / the legacy API. That is why this is a separate service instead of
 * a method on TopicService, whose `url` is mutated into the legacy term/stage path.
 */
@Injectable()
export class TopicV2Service {
  private readonly url = environment.V2_API_URL + '/topics'
  private readonly autocompletesUrl = environment.V2_API_URL + '/autocompletes'

  constructor (
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {
  }

  public search (params: QueryString): Observable<CrudSearchResult<Topic>> {
    return this.http
        .get(this.url, { params: this.toV2Params(params), headers: this.headers() })
        .map((json: any) => this.toSearchResult(json))
  }

  public autocompleteStudents (query: string): Observable<AutocompleteItem[]> {
    return this.autocomplete('students', query)
  }

  public autocompleteAuthors (query: string): Observable<AutocompleteItem[]> {
    return this.autocomplete('authors', query)
  }

  private autocomplete (resource: 'students' | 'authors', query: string): Observable<AutocompleteItem[]> {
    return this.http
        .get(this.autocompletesUrl + '/' + resource, {
          params: new HttpParams().set('query', query || ''),
          headers: this.headers()
        })
        .map((json: any) => (json || []) as AutocompleteItem[])
  }

  private headers (): HttpHeaders {
    const stage = this.authService.getSelectedStage()
    // the JWT interceptor only covers environment.API_HOST, and v2 lives on another
    // host in development, so the token is attached explicitly (setHeaders is idempotent)
    return new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getCurrentToken(),
      'X-Stage': stage ? stage._id : ''
    })
  }

  private toV2Params (params: QueryString): HttpParams {
    const conditions = params.conditions || {}
    let search = new HttpParams()
        .set('page', String(params.page || 1))
        .set('pageSize', String(params.limit || 10))

    const title = (params.q || '').trim()
    if (title) {
      search = search.set('title', title)
    }

    const type = this.singleType(conditions.type)
    if (type) {
      search = search.set('type', type)
    }

    const authors = this.toArray(conditions.createdBy)
    if (authors.length) {
      search = search.set('author', authors.join(','))
    }

    // v2 expands every student into [student, their division, stage], so a topic matches
    // whether it was sent to the student, to their course or to the whole stage
    const students = this.toArray(conditions.students)
    if (students.length) {
      search = search.set('students', students.join(','))
    }

    const from = conditions.timestamp && conditions.timestamp.$gt
    if (typeof from === 'number') {
      search = search.set('fromDateTime', String(from))
    }
    const to = conditions.timestamp && conditions.timestamp.$lt
    if (typeof to === 'number') {
      search = search.set('toDateTime', String(to))
    }

    const sort = this.toV2Sort(params.sort)
    if (sort) {
      search = search.set('sort', sort)
    }

    return search
  }

  // The type filter is either "all newsfeed types" or a single one. v2 already defaults to
  // all of them, so only a single selection has to be sent.
  private singleType (typeCondition: any): string | undefined {
    const types = typeCondition && typeCondition.$in
    return types && types.length === 1 ? types[0] : undefined
  }

  private toArray (value: any): string[] {
    if (!value) {
      return []
    }
    return Array.isArray(value) ? value : [value]
  }

  private toV2Sort (sort: any): string | undefined {
    const props = Object.keys(sort || {})
    if (props.length !== 1) {
      return undefined
    }
    const field = SORT_FIELDS[props[0]]
    if (!field) {
      return undefined
    }
    return field + ':' + (sort[props[0]] < 0 ? 'desc' : 'asc')
  }

  private toSearchResult (json: any): CrudSearchResult<Topic> {
    const docs = (json && json.docs) || []
    return new CrudSearchResult(
        docs.map((doc: any) => Topic.fromJson(doc)),
        new CrudSearchResultMeta(json.page || 1, json.limit || docs.length, json.totalDocs || 0))
  }
}
